import {Box, Typography} from '@mui/material';
import {useCallback, useMemo} from 'react';
import {
  DragAndDropUploadStatus,
  UploadQueueItem,
} from '../../../../../types/upload-new-eviidence/evidenceSourceTypes';
import AurDataGridPro from '../../../../common/aur-data-grid-pro/AurDataGridPro';
import {t} from 'i18next';
import {GridRenderCellParams} from '@mui/x-data-grid';
import AurButton from '../../../../common/aur-button/AurButton';
import AurBarStripe from '../../../../common/aur-bar-stripe/AurBarStripe';
import {formatBytes} from '../../../../../utils/utils';

interface FileCatalystGridProps {
  type: 'in-progress' | 'completed';
  title: string;
  emptyMessage: string;
  data: UploadQueueItem[];
  onCancel?: (id: string) => void;
  onRetry?: (id: string) => void;
  onRemove?: (id: string) => void;
}

const FileCatalystGrid = ({
  type,
  title,
  emptyMessage,
  data,
  onCancel,
  onRetry,
  onRemove,
}: FileCatalystGridProps) => {
  const columns = useMemo(() => {
    const initialColums = [
      {
        field: 'fileName',
        headerName: t('fileCatalyst.fileCatalystGrid.fileName'),
        flex: type === 'in-progress' ? 0.35 : 0.45,
        sortable: false,
        filterable: false,
        headerClassName: 'no-separator',
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box className={'row-container'} tabIndex={0}>
              <Typography className="typo-text body3_size grid-row-text">
                {params.row?.file?.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'status',
        headerName: t('fileCatalyst.fileCatalystGrid.status'),
        flex: 0.1,
        sortable: false,
        filterable: false,
        headerClassName: 'no-separator',
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box className={'row-container'} tabIndex={0}>
              <Typography
                className={`typo-text body3_size grid-row-text ${params.row?.status === DragAndDropUploadStatus.Failed ? 'failed-text-color' : ''}`}
              >
                {t(`fileCatalyst.fileCatalystGrid.${params.row?.status}`)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'size',
        headerName: t('fileCatalyst.fileCatalystGrid.size'),
        flex: 0.1,
        sortable: false,
        filterable: false,
        headerClassName: 'no-separator',
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box className={'row-container'} tabIndex={0}>
              <Typography className="typo-text body3_size grid-row-text">
                {formatBytes(params.row?.file?.size, 1)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'overall-progress',
        headerName: t('fileCatalyst.fileCatalystGrid.overallProgress'),
        flex: 0.3,
        sortable: false,
        filterable: false,
        headerClassName: 'no-separator',
        disableTooltip: true,
        renderCell: (params: GridRenderCellParams) => {
          const isFailed =
            params.row.progress === 0 &&
            params.row.status === DragAndDropUploadStatus.Failed;
          return (
            <Box className={'row-container'} tabIndex={0}>
              <AurBarStripe
                state={params.row.status}
                percent={isFailed ? 50 : params.row.progress}
              />
            </Box>
          );
        },
      },
      {
        field: 'actions',
        width: 160,
        sortable: false,
        filterable: false,
        isBorderNotRequired: true,
        disableTooltip: true,
        renderCell: (params: GridRenderCellParams) => {
          let buttonLabel: string;

          if (params.row.status === DragAndDropUploadStatus.Completed) {
            buttonLabel = t('fileCatalyst.fileCatalystGrid.removeFile');
          } else if (params.row.status === DragAndDropUploadStatus.Failed) {
            buttonLabel = t('fileCatalyst.fileCatalystGrid.retryUpload');
          } else {
            buttonLabel = t('fileCatalyst.fileCatalystGrid.cancelUpload');
          }
          return (
            <Box className={'row-container'}>
              <AurButton
                dataTestId={'action-button'}
                label={buttonLabel}
                ariaLabel={buttonLabel}
                type={'text'}
                size="Small"
                onClick={() => {
                  if (!params.row?.id) return;
                  if (params.row.status === DragAndDropUploadStatus.Completed) {
                    onRemove?.(params.row.id);
                  } else if (
                    params.row.status === DragAndDropUploadStatus.Failed
                  ) {
                    onRetry?.(params.row.id);
                  } else {
                    onCancel?.(params.row.id);
                  }
                }}
              />
            </Box>
          );
        },
      },
    ];

    return type === 'completed'
      ? initialColums.filter((item) => item.field !== 'status')
      : initialColums;
  }, [onCancel, onRemove, onRetry, type]);

  const renderGrid = useCallback(() => {
    return (
      <AurDataGridPro
        className="file-catalyst-grid"
        rows={data}
        getRowId={(row) => row.id}
        columns={columns}
        rowHeight={56}
        checkBoxVisible={false}
        hideFooter
        headerFilterHeight={0}
        noRowsOverlay={undefined}
        visibleToolTip={true}
        paginationModel={{
          page: 0,
          pageSize: data.length,
        }}
      />
    );
  }, [columns, data]);

  const renderEmptyState = useCallback(() => {
    return (
      <Box className={'empty-container'}>
        <Typography
          tabIndex={0}
          className="typo-text body1_size drag-and-drop-desc"
        >
          {emptyMessage}
        </Typography>
      </Box>
    );
  }, [emptyMessage]);

  return (
    <Box className={'file-section-container'}>
      <Typography
        tabIndex={0}
        className="typo-text subhead2_size section-title"
      >
        {title}
      </Typography>
      {data.length > 0 ? renderGrid() : renderEmptyState()}
    </Box>
  );
};

export {FileCatalystGrid};
